import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldAlert, KeyRound, Loader2 } from "lucide-react";

/**
 * Full-screen blocker shown when `user.must_change_password === true`.
 * User cannot navigate the app until they rotate the password.
 */
export default function ForceChangePassword() {
  const { refresh, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 12) return toast.error("New password must be at least 12 characters");
    if (next !== confirm) return toast.error("Passwords don't match");
    if (next === current) return toast.error("New password must differ from the current one");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated. All other sessions have been signed out.");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to change password");
    } finally { setSaving(false); }
  };

  return (
    <div className="os-app" data-testid="force-password-change">
      <div className="min-h-screen flex items-center justify-center p-6">
        <form onSubmit={submit} className="os-card w-full max-w-md p-8 space-y-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{background:"rgba(245,158,11,0.15)", color:"var(--os-amber)"}}>
              <ShieldAlert className="w-5 h-5" />
            </div>
            <div>
              <div className="os-badge os-badge-amber mb-2">Security action required</div>
              <h2 className="text-xl font-bold" style={{color:"var(--os-text)"}}>Rotate your password to continue</h2>
              <p className="text-sm mt-1" style={{color:"var(--os-text-muted)"}}>
                This account is using a seed / temporary password. Pick a fresh one — at least 12 characters. All your other sessions will be signed out.
              </p>
            </div>
          </div>

          <label className="block">
            <div className="text-xs mb-1" style={{color:"var(--os-text-muted)"}}>Current password</div>
            <input type="password" required className="os-input" value={current} onChange={e => setCurrent(e.target.value)}
              data-testid="fp-current" autoFocus autoComplete="current-password" />
          </label>
          <label className="block">
            <div className="text-xs mb-1" style={{color:"var(--os-text-muted)"}}>New password (min 12 chars)</div>
            <input type="password" required minLength={12} className="os-input" value={next} onChange={e => setNext(e.target.value)}
              data-testid="fp-new" autoComplete="new-password" />
            <div className="text-[11px] mt-1" style={{color: next && next.length >= 12 ? "var(--os-green)" : "var(--os-text-dim)"}}>
              {next ? `${next.length}/12+ chars` : "Mix letters, numbers, and symbols"}
            </div>
          </label>
          <label className="block">
            <div className="text-xs mb-1" style={{color:"var(--os-text-muted)"}}>Confirm new password</div>
            <input type="password" required minLength={12} className="os-input" value={confirm} onChange={e => setConfirm(e.target.value)}
              data-testid="fp-confirm" autoComplete="new-password" />
          </label>

          <button type="submit" disabled={saving} className="os-btn w-full justify-center h-10" data-testid="fp-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            {saving ? "Rotating…" : "Set new password"}
          </button>

          <button type="button" onClick={logout} className="w-full text-xs text-center" style={{color:"var(--os-text-dim)"}} data-testid="fp-signout">
            Sign out instead
          </button>
        </form>
      </div>
    </div>
  );
}
